// Initial-observed activation checks for `?!` rules in main style output.
// They gate rule emission by current DOM existence and runtime media/support conditions.
import { failClosedSecurityCheck } from '@lentystyle/guard/runtime'

// Resolve initial-observed rules that should be emitted on current load.
export function resolveActiveInitialObservedRules(
  initialObservedRules: Array<{ selector: string; atRules: string[]; key: string; rule: string }>,
): Array<{ selector: string; atRules: string[]; key: string; rule: string }> {
  // Keep empty list when source has no initial-observed rules.
  if (initialObservedRules.length === 0) {
    return []
  } else {
    // Continue initial-observed activation filtering.
  }

  const activeRules: Array<{ selector: string; atRules: string[]; key: string; rule: string }> = []
  // Keep only rules whose selector exists and conditions currently pass.
  for (const rule of initialObservedRules) {
    // Skip unsafe selector text before runtime querySelector lookup.
    if (!isSafeRuntimeSelector(rule.selector)) {
      continue
    } else {
      // Continue initial-observed activation checks for safe selectors.
    }

    let selectorExists = false
    try {
      selectorExists = document.querySelector(rule.selector) !== null
    } catch {
      // Skip invalid selector text and continue with next initial-observed rule.
      continue
    }

    if (!selectorExists) {
      continue
    } else if (!evaluateInitialObservedAtRules(rule.atRules)) {
      continue
    } else {
      activeRules.push(rule)
    }
  }

  // Return initial-observed rules active on this compile cycle.
  return activeRules
}

// Evaluate initial-observed at-rule conditions for current runtime state.
function evaluateInitialObservedAtRules(atRules: string[]): boolean {
  // Evaluate all supported at-rule conditions as AND-chain.
  for (const atRule of atRules) {
    const mediaQuery = parseInitialObservedMediaQuery(atRule)
    if (mediaQuery) {
      if (!window.matchMedia(mediaQuery).matches) {
        return false
      } else {
        continue
      }
    } else {
      // Continue supports condition checks.
    }

    const supportsQuery = parseInitialObservedSupportsQuery(atRule)
    if (supportsQuery) {
      if (!evaluateInitialObservedSupports(supportsQuery)) {
        return false
      } else {
        continue
      }
    } else {
      // Ignore non-conditional at-rules.
    }
  }

  // Return true when all supported conditions pass.
  return true
}

// Parse media query from at-rule token.
function parseInitialObservedMediaQuery(atRule: string): string | null {
  const trimmed = atRule.trim()
  // Reject non-@media at-rules.
  if (!trimmed.toLowerCase().startsWith('@media')) {
    return null
  } else {
    // Continue media query extraction.
  }
  const query = trimmed.slice(6).trim()
  // Return query text when present.
  return query.length > 0 ? query : null
}

// Parse supports condition from at-rule token.
function parseInitialObservedSupportsQuery(atRule: string): string | null {
  const trimmed = atRule.trim()
  // Reject non-@supports at-rules.
  if (!trimmed.toLowerCase().startsWith('@supports')) {
    return null
  } else {
    // Continue supports query extraction.
  }
  const query = trimmed.slice(9).trim()
  // Return query text when present.
  return query.length > 0 ? query : null
}

// Evaluate supports condition with safe fallback.
function evaluateInitialObservedSupports(query: string): boolean {
  // Return false when CSS.supports API is unavailable.
  if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') {
    return false
  } else {
    // Continue supports query evaluation.
  }

  try {
    return CSS.supports(query)
  } catch {
    // Return false for malformed supports conditions.
    return false
  }
}

// Validate runtime selector text before querySelector evaluation.
function isSafeRuntimeSelector(selector: string): boolean {
  try {
    // Reject selectors containing html/script breakout payloads.
    if (/<\/style|<script|<\/script|javascript\s*:/i.test(selector)) {
      return false
    } else if (selector.length > 4096) {
      // Reject oversized selectors to reduce pathological scans.
      return false
    } else {
      // Return true for selector text passing runtime safety checks.
      return true
    }
  } catch (error) {
    // Stop compile flow when runtime-selector sanitizer cannot execute.
    return failClosedSecurityCheck('isSafeRuntimeSelector', error)
  }
}
